import { mapExternalRecord } from "@/features/league/externalDataLogic";
import { shortNameFromFullName, shortNameFromParts } from "@/models/athleteUniversalFields";
import { countryResourceMatching, fetchCountryResources, type CountryResource } from "@/models/countryResource";
import { dynamicValueDisplayText, type LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import type { LeagueAthleteUniversalFieldRole } from "@/models/fieldDefinition";
import type { LeagueExternalDataSource } from "@/models/externalSource";
import type { OverlayLowerThirdPerson } from "@/models/overlayConfig";
import { athleteValueAllValues, type LeagueAthleteValue, type LeagueFieldSchema } from "@/models/schema";
import { fetchExternalSourceRecords } from "@/services/externalSources";
import { getGoogleSheetsAccessToken } from "@/services/googleSheetsAuth";

// Person pickers for broadcast lower thirds. Both sources — a live-fetched
// external dataset (records are transient, never imported) and the league's
// imported athlete pool — resolve to the same PersonOption shape, so the
// designer UI is source-agnostic.

export const LEAGUE_ATHLETES_SOURCE_ID = "league-athletes";

export interface PersonExtraField {
  key: string;
  label: string;
  value: string;
}

export interface PersonOption {
  /** Stable dropdown value: participant-id / athlete doc id / record index. */
  recordKey: string;
  label: string;
  /** Broadcast snapshot minus the extra-* keys (bound separately). */
  person: OverlayLowerThirdPerson;
  /** Candidates for the accent sub-bar binding. */
  extraFields: PersonExtraField[];
  /** Typed mapped values by field ID — sortable, unlike extraFields' display strings. */
  values: Record<string, LeagueDynamicFieldValue>;
}

/**
 * Persisted snapshot with its short-name refreshed from a re-resolved option
 * (e.g. when short name mode turns on for people picked before the mapping
 * existed); null when the option is unavailable or nothing would change.
 */
export function personWithRefreshedShortName(
  person: OverlayLowerThirdPerson,
  option: PersonOption | undefined,
): OverlayLowerThirdPerson | null {
  if (!option || option.person["short-name"] === person["short-name"]) return null;
  return { ...person, "short-name": option.person["short-name"] };
}

function displayText(value: LeagueDynamicFieldValue | undefined): string | undefined {
  if (!value) return undefined;
  const text = dynamicValueDisplayText(value);
  return text && text !== "—" ? text : undefined;
}

const PERSON_ROLES: LeagueAthleteUniversalFieldRole[] = ["full-name", "broadcast-name", "short-name", "flag-url", "headshot-url", "bib", "participant-id"];

/** Fetches + maps an external dataset's records via its saved mapping profile. */
export async function personOptionsFromExternalSource(
  leagueID: string,
  source: LeagueExternalDataSource,
): Promise<PersonOption[]> {
  const googleAccessToken =
    source.sourceKind === "googleSheets" && source.accessMode === "private" ? await getGoogleSheetsAccessToken() : undefined;
  const [response, countries] = await Promise.all([
    fetchExternalSourceRecords({ leagueID, sourceID: source.id, googleAccessToken }),
    fetchCountryResources().catch(() => []),
  ]);

  return personOptionsFromExternalRecords(response.records, source, countries);
}

/** Maps shared Firestore-cached source records using the source's saved profile. */
export function personOptionsFromExternalRecords(
  records: Record<string, unknown>[],
  source: LeagueExternalDataSource,
  countries: CountryResource[] = [],
): PersonOption[] {

  const fieldIDForRole = (role: LeagueAthleteUniversalFieldRole): string | undefined =>
    source.mappingProfile.mappings.find((mapping) => mapping.targetUniversalRole === role)?.targetFieldID;

  return records.flatMap((record, index) => {
    const mapped = mapExternalRecord(record, source.mappingProfile);
    const textForRole = (role: LeagueAthleteUniversalFieldRole) => {
      const fieldID = fieldIDForRole(role);
      return fieldID ? displayText(mapped.values[fieldID]) : undefined;
    };

    const fullName = textForRole("full-name") ?? textForRole("broadcast-name") ?? textForRole("short-name");
    if (!fullName) return [];

    const shortName =
      textForRole("short-name") ??
      shortNameFromParts(textForRole("first-name"), textForRole("last-name")) ??
      shortNameFromFullName(fullName);
    // Timing feeds rarely carry a flag column; derive one from the mapped
    // country name/code so transient sources match imported athletes.
    const flagURL = textForRole("flag-url") ?? derivedFlagURL(textForRole("country-name") ?? textForRole("country-code"), countries);
    const bib = textForRole("bib");
    const extraFields = source.mappingProfile.mappings.flatMap((mapping) => {
      if (mapping.targetUniversalRole && PERSON_ROLES.includes(mapping.targetUniversalRole)) return [];
      const value = displayText(mapped.values[mapping.targetFieldID]);
      return value ? [{ key: mapping.targetFieldID, label: mapping.targetLabel || mapping.targetFieldID, value }] : [];
    });

    return [
      {
        recordKey: textForRole("participant-id") ?? `record-${index}`,
        label: bib ? `${fullName} · ${bib}` : fullName,
        person: { "full-name": fullName, "short-name": shortName, "flag-url": flagURL, "headshot-url": textForRole("headshot-url"), bib },
        extraFields,
        values: mapped.values,
      },
    ];
  });
}

function derivedFlagURL(countryText: string | undefined, countries: CountryResource[]): string | undefined {
  return countryText ? countryResourceMatching(countryText, countries)?.flagURL : undefined;
}

/** Builds options from the league's imported athlete pool (active records). */
export function personOptionsFromAthletes(
  athletes: LeagueAthleteValue[],
  schema: LeagueFieldSchema | null,
): PersonOption[] {
  const fields = schema?.fields ?? [];
  const labelByFieldID = new Map(fields.map((field) => [field.id, field.label]));
  const fieldIDByRole = new Map<LeagueAthleteUniversalFieldRole, string>();
  for (const field of fields) {
    if (field.universalAthleteRole && !fieldIDByRole.has(field.universalAthleteRole)) {
      fieldIDByRole.set(field.universalAthleteRole, field.id);
    }
  }
  const personFieldIDs = new Set(
    PERSON_ROLES.map((role) => fieldIDByRole.get(role)).filter((id): id is string => !!id),
  );

  return athletes
    .filter((athlete) => athlete.status === "active")
    .map((athlete) => {
      const values = athleteValueAllValues(athlete);
      const textForRole = (role: LeagueAthleteUniversalFieldRole) => {
        const fieldID = fieldIDByRole.get(role);
        return fieldID ? displayText(values[fieldID]) : undefined;
      };

      const fullName = textForRole("full-name") ?? textForRole("broadcast-name") ?? athlete.name;
      const shortName =
        textForRole("short-name") ??
        shortNameFromParts(textForRole("first-name"), textForRole("last-name")) ??
        shortNameFromFullName(fullName);
      const bib = textForRole("bib");
      const extraFields = Object.entries(values).flatMap(([fieldID, rawValue]) => {
        if (personFieldIDs.has(fieldID)) return [];
        const value = displayText(rawValue);
        return value ? [{ key: fieldID, label: labelByFieldID.get(fieldID) ?? fieldID, value }] : [];
      });

      return {
        recordKey: athlete.id,
        label: bib ? `${fullName} · ${bib}` : fullName,
        person: { "full-name": fullName, "short-name": shortName, "flag-url": textForRole("flag-url"), "headshot-url": textForRole("headshot-url"), bib },
        extraFields,
        values,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
